import React, { useEffect, useState } from 'react';
import { findAccountEdgeCandidates, teachAccountEdgeNumber } from './lineFix';

/*
  "WHICH ACCOUNT EDGE ITEM?", 11 October 2026, his ask on JOB-00003.

  For a line whose item has no Account Edge number. It searches the newest
  Account Edge list he uploaded, starting from the line's ClaimSoft number and
  the item's vendor part number, and he picks the Account Edge item. Its number
  is saved onto the OnHand item for good, so the Account Edge file can be built
  and every later job with that item already has it.
*/
export default function AePicker({ line, userId, onDone, onBack }) {
  const vendorPart = line.item ? line.item.vendor_part_number : '';
  const [text, setText] = useState(line.item_as_given);
  const [found, setFound] = useState(null);
  const [failed, setFailed] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let alive = true;
    setFound(null);
    const t = setTimeout(async () => {
      const r = await findAccountEdgeCandidates({ text, prefer: [line.item_as_given, vendorPart] });
      if (!alive) return;
      if (r.error) { setFailed(r.error.message); setFound([]); return; }
      setFailed(null);
      setFound(r.items);
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [text, line.item_as_given, vendorPart]);

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const r = await teachAccountEdgeNumber({ aeNumber: chosen.number, catalogItemId: line.catalog_item_id, userId });
    setSaving(false);
    if (!r.ok) { setProblem(r.error ? r.error.message : 'That did not save.'); return; }
    onDone({ ...r, ae: chosen.number });
  };

  const item = line.item || {};

  if (chosen) {
    return (
      <div>
        <p style={{ marginTop: 0 }}>
          <b>{line.vendor ? line.vendor.name : ''} {item.vendor_part_number}</b> ({item.our_description || item.vendor_description || line.description}) is Account Edge
        </p>
        <div className="card" style={{ padding: '12px 14px', marginBottom: 12 }}>
          <span className="rpnum" style={{ fontWeight: 700 }}>{chosen.number}</span> {chosen.name}
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            {[chosen.vendor, chosen.vendorItem ? 'vendor item ' + chosen.vendorItem : null, chosen.unit].filter(Boolean).join(' · ')}
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Saved onto the item for good, as its Account Edge number. Every open job with this item is checked again.
        </p>
        {problem && <p style={{ color: 'var(--critical-ink)' }}>{problem}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-quiet" disabled={saving} onClick={() => { setChosen(null); setProblem(null); }}>Not this one</button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Yes, save it'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
        <div>
          <b>Which Account Edge item is ClaimSoft <span className="rpnum">{line.item_as_given}</span>?</b>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            {[line.vendor ? line.vendor.name + ' ' + (item.vendor_part_number || '') : null, item.our_description || item.vendor_description || line.description].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button type="button" className="btn btn-quiet" onClick={onBack}>Back to the lines</button>
      </div>
      <input className="fld" style={{ width: '100%', marginBottom: 10 }} value={text} autoFocus
        aria-label="Search the Account Edge list" placeholder="An Account Edge item number, vendor item number or name"
        onChange={e => setText(e.target.value)} />
      {failed && <p style={{ color: 'var(--critical-ink)' }}>OnHand could not search the Account Edge list just now: {failed}</p>}
      {found === null ? (
        <p style={{ color: 'var(--text-secondary)' }}>Looking...</p>
      ) : found.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>Nothing in the Account Edge list matches that. Try its Account Edge number or a word from its name.</p>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {found.map(r => (
            <button key={r.number} type="button" className="btn btn-quiet" disabled={r.taken}
              title={r.taken ? 'This Account Edge number is already on another item' : undefined}
              style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: '8px 12px', whiteSpace: 'normal' }}
              onClick={() => setChosen(r)}>
              <span style={{ minWidth: 0 }}>
                <b className="rpnum">{r.number}</b> {r.name}
                {r.taken && <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>already on another item</span>}
                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {[r.vendor, r.vendorItem ? 'vendor item ' + r.vendorItem : null, r.unit].filter(Boolean).join(' · ')}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
